import TextField from '@mui/material/TextField';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import { useRef, useState } from 'react';
import { useQuery } from 'react-query';

export interface ComboBoxOption {
  label: string;
  value: number;
  inputValue?: string;
}

interface ComboxBoxProps {
  label: string;
  name: string;
  value?: ComboBoxOption | null;
  onChange: (option: ComboBoxOption | null) => void;
  options: ComboBoxOption[];
  error?: string;

  /** If a ComboBox is creatable, it will display a create drop down option */
  creatable?: boolean;
  onCreatableClick?: (nameSuggestion: string) => void;
}

export default function ComboBox(props: ComboxBoxProps) {
  const { label, options, name, value, onChange, error, creatable = false, onCreatableClick = function () {} } = props;
  const [option, setOption] = useState<ComboBoxOption | null | undefined>(null);
  const filter = createFilterOptions<ComboBoxOption>();
  const autoSelect = useRef(true);

  const onChangeHandler = (option: ComboBoxOption | null) => {
    if (option && option.inputValue !== undefined) {
      onCreatableClick(option.inputValue ?? '');
    } else {
      setOption(option);
      onChange(option);
    }
  };

  /** Sets the combo box value  */
  useQuery(`${value?.label}`, () => {
    setOption(value ? value : null);
  });

  return (
    <div>
      <Autocomplete
        autoHighlight={true}
        autoSelect={autoSelect.current}
        value={option}
        autoComplete={true}
        options={options}
        onChange={(_, value) => onChangeHandler(value)}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        clearOnEscape
        noOptionsText={creatable ? 'Press any key to ' : 'No Options'}
        filterOptions={(options, params) => {
          const filtered = filter(options, params);
          if (!creatable) return filtered;

          autoSelect.current = filtered.length !== 0;
          const input = params.inputValue;

          filtered.push({
            inputValue: input ?? '',
            label: `+ Create new ${name}` + (input.length > 0 ? ` (${input})` : ''),
            value: 0,
          });

          return filtered;
        }}
        renderInput={params => (
          <TextField {...params} label={label} name={name} {...(error && { error: true, helperText: error })} />
        )}
      />
    </div>
  );
}
